/**
 * Domain enumerations for the Elixir Finance Ops platform.
 *
 * These are the single source of truth shared by the API server and the web
 * application. They are declared as const objects
 * plus a derived union type so they can be used both as runtime values
 * (Mongoose enums, Zod enums, UI dropdowns) and as compile-time types.
 */
export const RoleKey = {
    PLATFORM_ADMIN: 'PLATFORM_ADMIN',
    COMPANY_ADMIN: 'COMPANY_ADMIN',
    FINANCE_EXECUTIVE: 'FINANCE_EXECUTIVE',
    FINANCE_MANAGER: 'FINANCE_MANAGER',
    APPROVER: 'APPROVER',
    VERTICAL_HEAD: 'VERTICAL_HEAD',
    ACCOUNTS_TEAM: 'ACCOUNTS_TEAM',
    TREASURY: 'TREASURY',
    CFO: 'CFO',
    PAYROLL_USER: 'PAYROLL_USER',
    AUDITOR: 'AUDITOR',
    /** Keeps the employee master. No payroll, invoice or payment access. */
    HR: 'HR',
    /**
     * An outside party, not a finance user: a vendor's own login, which sees the
     * invoices it billed and nothing else. Linked to vendor master rows through
     * `User.vendorIds` rather than through an organisation scope.
     */
    VENDOR: 'VENDOR',
};
export const ROLE_KEYS = Object.values(RoleKey);
/**
 * The roles that work inside the finance operation. Approval steps and other
 * "who on staff does this" lists draw from here, so a vendor's own login can
 * never be named as an approver of an invoice.
 */
export const INTERNAL_ROLE_KEYS = ROLE_KEYS.filter((key) => key !== RoleKey.VENDOR);
export const ROLE_LABELS = {
    PLATFORM_ADMIN: 'Platform Admin',
    COMPANY_ADMIN: 'Company Admin',
    FINANCE_EXECUTIVE: 'Finance Executive',
    FINANCE_MANAGER: 'Finance Manager',
    APPROVER: 'Approver',
    VERTICAL_HEAD: 'Vertical Head',
    ACCOUNTS_TEAM: 'Accounting Team',
    TREASURY: 'Treasury',
    CFO: 'CFO',
    PAYROLL_USER: 'Payroll User',
    AUDITOR: 'Auditor / Read Only',
    HR: 'HR',
    VENDOR: 'Vendor',
};
/**
 * Display name for a role key.
 *
 * Tenants can define their own roles, whose keys are not in `ROLE_LABELS`, so
 * every screen that prints a role goes through here rather than indexing the
 * map directly and rendering `undefined`.
 */
export function roleLabel(key, catalogue) {
    return (catalogue?.[key] ??
        ROLE_LABELS[key] ??
        key
            .toLowerCase()
            .split('_')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' '));
}
/** Invoice lifecycle — PRD §14. */
export const InvoiceStatus = {
    RECEIVED: 'RECEIVED',
    EXTRACTING: 'EXTRACTING',
    REVIEW_REQUIRED: 'REVIEW_REQUIRED',
    VALIDATED: 'VALIDATED',
    SUBMITTED: 'SUBMITTED',
    PENDING_APPROVAL: 'PENDING_APPROVAL',
    /** Business approval is done; the accounting team verifies and applies TDS. */
    ACCOUNTING_VERIFICATION: 'ACCOUNTING_VERIFICATION',
    /** Escalated to the Treasury team, with a priority and remarks. */
    TREASURY_APPROVAL: 'TREASURY_APPROVAL',
    APPROVED: 'APPROVED',
    PAYMENT_PENDING: 'PAYMENT_PENDING',
    PAYMENT_BATCHED: 'PAYMENT_BATCHED',
    PAYMENT_PROCESSING: 'PAYMENT_PROCESSING',
    PAID: 'PAID',
    RECONCILED: 'RECONCILED',
    /**
     * Paid in full from the vendor's advance balance, so no money leaves the
     * bank and there is no statement line to reconcile. Kept apart from PAID,
     * which still means only one thing: matched to a real bank transaction.
     */
    SETTLED_BY_ADVANCE: 'SETTLED_BY_ADVANCE',
    // Alternative / terminal states
    REJECTED: 'REJECTED',
    DUPLICATE: 'DUPLICATE',
    CANCELLED: 'CANCELLED',
    FAILED: 'FAILED',
};
export const INVOICE_STATUSES = Object.values(InvoiceStatus);
/** Statuses that mean the invoice is no longer progressing towards payment. */
export const INVOICE_TERMINAL_STATUSES = [
    InvoiceStatus.REJECTED,
    InvoiceStatus.DUPLICATE,
    InvoiceStatus.CANCELLED,
    InvoiceStatus.FAILED,
    InvoiceStatus.RECONCILED,
    InvoiceStatus.SETTLED_BY_ADVANCE,
];
/**
 * What a vendor is told about an invoice it billed.
 *
 * Deliberately coarser than `InvoiceStatus`: review, accounting verification
 * and treasury escalation are how the finance team works, not something the
 * vendor acts on, so they all read as "under review".
 */
export const VendorInvoiceStatus = {
    RECEIVED: 'RECEIVED',
    UNDER_REVIEW: 'UNDER_REVIEW',
    APPROVED: 'APPROVED',
    PAYMENT_SCHEDULED: 'PAYMENT_SCHEDULED',
    PAID: 'PAID',
    REJECTED: 'REJECTED',
    CANCELLED: 'CANCELLED',
};
export const VENDOR_INVOICE_STATUSES = Object.values(VendorInvoiceStatus);
export const VENDOR_INVOICE_STATUS_LABELS = {
    RECEIVED: 'Received',
    UNDER_REVIEW: 'Under review',
    APPROVED: 'Approved',
    PAYMENT_SCHEDULED: 'Payment scheduled',
    PAID: 'Paid',
    REJECTED: 'Rejected',
    CANCELLED: 'Cancelled',
};
/**
 * A full record, so a new `InvoiceStatus` does not compile until someone
 * decides what the vendor is told about it.
 */
const VENDOR_STATUS_BY_INVOICE_STATUS = {
    RECEIVED: VendorInvoiceStatus.RECEIVED,
    EXTRACTING: VendorInvoiceStatus.RECEIVED,
    REVIEW_REQUIRED: VendorInvoiceStatus.UNDER_REVIEW,
    VALIDATED: VendorInvoiceStatus.UNDER_REVIEW,
    SUBMITTED: VendorInvoiceStatus.UNDER_REVIEW,
    PENDING_APPROVAL: VendorInvoiceStatus.UNDER_REVIEW,
    ACCOUNTING_VERIFICATION: VendorInvoiceStatus.UNDER_REVIEW,
    TREASURY_APPROVAL: VendorInvoiceStatus.UNDER_REVIEW,
    APPROVED: VendorInvoiceStatus.APPROVED,
    PAYMENT_PENDING: VendorInvoiceStatus.APPROVED,
    PAYMENT_BATCHED: VendorInvoiceStatus.PAYMENT_SCHEDULED,
    PAYMENT_PROCESSING: VendorInvoiceStatus.PAYMENT_SCHEDULED,
    PAID: VendorInvoiceStatus.PAID,
    RECONCILED: VendorInvoiceStatus.PAID,
    // Settled against money the vendor already holds — to the vendor, paid.
    SETTLED_BY_ADVANCE: VendorInvoiceStatus.PAID,
    REJECTED: VendorInvoiceStatus.REJECTED,
    DUPLICATE: VendorInvoiceStatus.CANCELLED,
    CANCELLED: VendorInvoiceStatus.CANCELLED,
    // Extraction or payment failed and finance retries it; to the vendor the
    // invoice is still in hand, not refused.
    FAILED: VendorInvoiceStatus.UNDER_REVIEW,
};
export function vendorInvoiceStatus(status) {
    return VENDOR_STATUS_BY_INVOICE_STATUS[status];
}
/** The internal statuses one vendor-facing status covers, for filtering. */
export function invoiceStatusesFor(vendorStatus) {
    return INVOICE_STATUSES.filter((status) => VENDOR_STATUS_BY_INVOICE_STATUS[status] === vendorStatus);
}
export const InvoiceSource = {
    EMAIL: 'EMAIL',
    UPLOAD: 'UPLOAD',
};
/** Payroll batch lifecycle. Mirrors the invoice ladder at batch granularity. */
export const PayrollBatchStatus = {
    DRAFT: 'DRAFT',
    IMPORTED: 'IMPORTED',
    REVIEW_REQUIRED: 'REVIEW_REQUIRED',
    VALIDATED: 'VALIDATED',
    PENDING_APPROVAL: 'PENDING_APPROVAL',
    APPROVED: 'APPROVED',
    PAYMENT_PENDING: 'PAYMENT_PENDING',
    PAYMENT_BATCHED: 'PAYMENT_BATCHED',
    PAYMENT_PROCESSING: 'PAYMENT_PROCESSING',
    PAID: 'PAID',
    RECONCILED: 'RECONCILED',
    REJECTED: 'REJECTED',
    CANCELLED: 'CANCELLED',
};
export const PAYROLL_BATCH_STATUSES = Object.values(PayrollBatchStatus);
/** Payment batch lifecycle — PRD §22. */
export const PaymentBatchStatus = {
    DRAFT: 'DRAFT',
    READY: 'READY',
    EXPORTED: 'EXPORTED',
    PROCESSING: 'PROCESSING',
    COMPLETED: 'COMPLETED',
    PARTIALLY_RECONCILED: 'PARTIALLY_RECONCILED',
    RECONCILED: 'RECONCILED',
    CANCELLED: 'CANCELLED',
};
export const PAYMENT_BATCH_STATUSES = Object.values(PaymentBatchStatus);
/** What kind of source produced a payment obligation — PRD §20. */
export const ObligationType = {
    VENDOR: 'VENDOR',
    PAYROLL: 'PAYROLL',
    /** A loan EMI, processing fee or penal charge owed to a lender. */
    LOAN: 'LOAN',
};
/** Approval state, used on obligations, invoices and payroll batches. */
export const ApprovalStatus = {
    NOT_REQUIRED: 'NOT_REQUIRED',
    PENDING: 'PENDING',
    IN_PROGRESS: 'IN_PROGRESS',
    APPROVED: 'APPROVED',
    REJECTED: 'REJECTED',
    CANCELLED: 'CANCELLED',
};
export const ApprovalStepStatus = {
    PENDING: 'PENDING',
    ACTIVE: 'ACTIVE',
    APPROVED: 'APPROVED',
    REJECTED: 'REJECTED',
    SKIPPED: 'SKIPPED',
};
export const ApprovalSubjectType = {
    VENDOR_INVOICE: 'VENDOR_INVOICE',
    PAYROLL_BATCH: 'PAYROLL_BATCH',
};
export const ApproverType = {
    ROLE: 'ROLE',
    USER: 'USER',
    DEPARTMENT_HEAD: 'DEPARTMENT_HEAD',
    VERTICAL_HEAD: 'VERTICAL_HEAD',
};
export const APPROVER_TYPES = Object.values(ApproverType);
/**
 * A business unit that is only visible to users explicitly granted it.
 *
 * `CLASSIFIED` sits at the same level of the hierarchy as `STANDARD`, so it is
 * a discriminator on the business unit rather than a collection of its own.
 */
export const BusinessUnitKind = {
    STANDARD: 'STANDARD',
    CLASSIFIED: 'CLASSIFIED',
};
export const BUSINESS_UNIT_KINDS = Object.values(BusinessUnitKind);
/** Urgency a finance request carries to the Treasury team. */
export const FinanceRequestPriority = {
    P1: 'P1',
    P2: 'P2',
};
export const FINANCE_REQUEST_PRIORITIES = Object.values(FinanceRequestPriority);
/**
 * Treasury outcome. `RETURNED` sends the invoice back to accounting for rework
 * and has no equivalent in the approval-step vocabulary, which is one reason
 * finance requests are their own collection.
 */
export const FinanceRequestStatus = {
    PENDING: 'PENDING',
    APPROVED: 'APPROVED',
    REJECTED: 'REJECTED',
    RETURNED: 'RETURNED',
    CANCELLED: 'CANCELLED',
};
export const FINANCE_REQUEST_STATUSES = Object.values(FinanceRequestStatus);
export const FinanceRequestAction = {
    APPROVE: 'APPROVE',
    REJECT: 'REJECT',
    RETURN: 'RETURN',
};
/** The shape of a section code: three digits and up to three letters. */
export const TDS_SECTION_PATTERN = /^\d{3}[A-Z]{0,3}$/;
/**
 * The sections and rates a tenant starts with — the ones that occur on vendor
 * invoices, at the FY 2025-26 rates. Every one can be edited, switched off or
 * added to in Settings; the tax team owns them from then on.
 */
export const PREBUILT_TDS_SECTIONS = [
    {
        code: '194A',
        name: 'Interest other than on securities',
        rates: [{ label: 'Interest', rateBasisPoints: 1000 }],
    },
    {
        code: '194C',
        name: 'Payments to contractors',
        rates: [
            { label: 'Individual / HUF', rateBasisPoints: 100 },
            { label: 'Others', rateBasisPoints: 200 },
        ],
    },
    {
        code: '194H',
        name: 'Commission or brokerage',
        rates: [{ label: 'Commission', rateBasisPoints: 200 }],
    },
    {
        code: '194I',
        name: 'Rent',
        rates: [
            { label: 'Plant & machinery', rateBasisPoints: 200 },
            { label: 'Land, building or furniture', rateBasisPoints: 1000 },
        ],
    },
    {
        code: '194IA',
        name: 'Transfer of immovable property',
        rates: [{ label: 'Property', rateBasisPoints: 100 }],
    },
    {
        code: '194IB',
        name: 'Rent paid by individuals or HUF',
        rates: [{ label: 'Rent', rateBasisPoints: 200 }],
    },
    {
        code: '194J',
        name: 'Fees for professional or technical services',
        rates: [
            { label: 'Technical services / royalty', rateBasisPoints: 200 },
            { label: 'Professional fees', rateBasisPoints: 1000 },
        ],
    },
    {
        code: '194M',
        name: 'Contract work, commission or fees paid by individuals or HUF',
        rates: [{ label: 'Payments', rateBasisPoints: 200 }],
    },
    {
        code: '194O',
        name: 'E-commerce participants',
        rates: [{ label: 'Sales', rateBasisPoints: 10 }],
    },
    { code: '194Q', name: 'Purchase of goods', rates: [{ label: 'Goods', rateBasisPoints: 10 }] },
    {
        code: '194R',
        name: 'Business perquisites',
        rates: [{ label: 'Perquisites', rateBasisPoints: 1000 }],
    },
    {
        code: '194T',
        name: 'Payments to partners',
        rates: [{ label: 'Remuneration', rateBasisPoints: 1000 }],
    },
    {
        code: '195',
        name: 'Payments to non-residents',
        rates: [
            { label: 'Fees / royalty', rateBasisPoints: 1000 },
            { label: 'Other sums', rateBasisPoints: 2000 },
        ],
    },
];
/** Where an obligation sits in the payment pipeline — PRD §20. */
export const PaymentStatus = {
    PENDING: 'PENDING',
    QUEUED: 'QUEUED',
    BATCHED: 'BATCHED',
    PROCESSING: 'PROCESSING',
    PAID: 'PAID',
    FAILED: 'FAILED',
    CANCELLED: 'CANCELLED',
    ON_HOLD: 'ON_HOLD',
};
/** Reconciliation state — PRD §26. */
export const ReconciliationStatus = {
    UNMATCHED: 'UNMATCHED',
    SUGGESTED: 'SUGGESTED',
    MATCHED: 'MATCHED',
    IGNORED: 'IGNORED',
};
export const MatchMethod = {
    AUTO_SUGGESTED: 'AUTO_SUGGESTED',
    MANUAL: 'MANUAL',
};
export const BankTransactionDirection = {
    DEBIT: 'DEBIT',
    CREDIT: 'CREDIT',
};
/** Severity of a validation finding — PRD §13. */
export const ValidationSeverity = {
    ERROR: 'ERROR',
    WARNING: 'WARNING',
    INFO: 'INFO',
};
export const ValidationCode = {
    MISSING_VENDOR: 'MISSING_VENDOR',
    MISSING_INVOICE_NUMBER: 'MISSING_INVOICE_NUMBER',
    MISSING_INVOICE_DATE: 'MISSING_INVOICE_DATE',
    MISSING_TOTAL: 'MISSING_TOTAL',
    TOTAL_MISMATCH: 'TOTAL_MISMATCH',
    NEGATIVE_AMOUNT: 'NEGATIVE_AMOUNT',
    POSSIBLE_DUPLICATE: 'POSSIBLE_DUPLICATE',
    EXACT_DUPLICATE: 'EXACT_DUPLICATE',
    LOW_CONFIDENCE_FIELD: 'LOW_CONFIDENCE_FIELD',
    MISSING_VENDOR_BANK_DETAILS: 'MISSING_VENDOR_BANK_DETAILS',
    DUE_DATE_BEFORE_INVOICE_DATE: 'DUE_DATE_BEFORE_INVOICE_DATE',
    /** The document is billed in a currency other than its company's. */
    CURRENCY_MISMATCH: 'CURRENCY_MISMATCH',
    /** The vendor (or the invoice) carries the company's own GSTIN — the buyer's, not the supplier's. */
    GSTIN_SAME_AS_COMPANY: 'GSTIN_SAME_AS_COMPANY',
    /** The PAN on the invoice is not the one in the vendor master. */
    VENDOR_PAN_MISMATCH: 'VENDOR_PAN_MISMATCH',
};
export const VendorStatus = {
    ACTIVE: 'ACTIVE',
    INACTIVE: 'INACTIVE',
    BLOCKED: 'BLOCKED',
};
/**
 * One movement on a vendor's advance balance. Deposits and top-ups credit it,
 * an adjustment against an invoice debits it, and a reversal mirrors an
 * earlier entry — entries are never edited or deleted.
 */
export const VendorAdvanceEntryType = {
    DEPOSIT: 'DEPOSIT',
    TOP_UP: 'TOP_UP',
    ADJUSTMENT: 'ADJUSTMENT',
    REVERSAL: 'REVERSAL',
};
export const VENDOR_ADVANCE_ENTRY_TYPES = Object.values(VendorAdvanceEntryType);
// ── Employee master (HR) ──────────────────────────────────
/** Where an employee works from. */
export const WorkMode = {
    OFFICE: 'OFFICE',
    REMOTE: 'REMOTE',
    HYBRID: 'HYBRID',
    CLIENT_SITE: 'CLIENT_SITE',
};
export const WORK_MODES = Object.values(WorkMode);
export const EmploymentType = {
    PERMANENT: 'PERMANENT',
    CONTRACT: 'CONTRACT',
    INTERN: 'INTERN',
    CONSULTANT: 'CONSULTANT',
};
export const EMPLOYMENT_TYPES = Object.values(EmploymentType);
/** An employee's place in their lifecycle. Leaving is a status, never a delete. */
export const EmployeeStatus = {
    ACTIVE: 'ACTIVE',
    NOTICE: 'NOTICE',
    RESIGNED: 'RESIGNED',
    TERMINATED: 'TERMINATED',
    LONG_LEAVE: 'LONG_LEAVE',
};
export const EMPLOYEE_STATUSES = Object.values(EmployeeStatus);
/** The statuses an employee has, or is about to have, a last working day in. */
export const LEAVING_EMPLOYEE_STATUSES = [
    EmployeeStatus.NOTICE,
    EmployeeStatus.RESIGNED,
    EmployeeStatus.TERMINATED,
];
export const AllocationType = {
    DEDICATED: 'DEDICATED',
    SHARED: 'SHARED',
};
export const ALLOCATION_TYPES = Object.values(AllocationType);
export const BillableFlag = {
    YES: 'YES',
    NO: 'NO',
    PARTIAL: 'PARTIAL',
};
export const BILLABLE_FLAGS = Object.values(BillableFlag);
/** How an advance reached the vendor. Recorded, not executed: it was paid outside the platform. */
export const VendorAdvanceMode = {
    NEFT: 'NEFT',
    RTGS: 'RTGS',
    IMPS: 'IMPS',
    CHEQUE: 'CHEQUE',
    UPI: 'UPI',
    OTHER: 'OTHER',
};
export const VENDOR_ADVANCE_MODES = Object.values(VendorAdvanceMode);
/** The five kinds of account a company's chart of accounts is built from. */
export const LedgerType = {
    ASSET: 'ASSET',
    LIABILITY: 'LIABILITY',
    INCOME: 'INCOME',
    EXPENSE: 'EXPENSE',
    EQUITY: 'EQUITY',
};
export const LEDGER_TYPES = Object.values(LedgerType);
/**
 * What a vendor's own account in the ledger is for. A vendor may hold several
 * in one company: what it is owed, what it was paid in advance, a deposit.
 */
export const LedgerPartyRole = {
    PAYABLE: 'PAYABLE',
    ADVANCE: 'ADVANCE',
    DEPOSIT: 'DEPOSIT',
    OTHER: 'OTHER',
};
export const LEDGER_PARTY_ROLES = Object.values(LedgerPartyRole);
/** What is owed to a vendor is a liability; what was paid to it ahead is an asset. */
export const PARTY_ROLE_LEDGER_TYPE = {
    PAYABLE: LedgerType.LIABILITY,
    ADVANCE: LedgerType.ASSET,
    DEPOSIT: LedgerType.ASSET,
    OTHER: LedgerType.LIABILITY,
};
export const UserStatus = {
    ACTIVE: 'ACTIVE',
    INVITED: 'INVITED',
    SUSPENDED: 'SUSPENDED',
};
export const BankFileFormat = {
    HDFC: 'HDFC',
    ICICI: 'ICICI',
    GENERIC_CSV: 'GENERIC_CSV',
    GENERIC_XLSX: 'GENERIC_XLSX',
};
export const StatementImportStatus = {
    UPLOADED: 'UPLOADED',
    PARSING: 'PARSING',
    PARSED: 'PARSED',
    FAILED: 'FAILED',
};
export const NotificationChannel = {
    IN_APP: 'IN_APP',
    EMAIL: 'EMAIL',
};
export const NotificationStatus = {
    PENDING: 'PENDING',
    SENT: 'SENT',
    READ: 'READ',
    FAILED: 'FAILED',
};
/** Notification types — PRD §34. */
export const NotificationType = {
    INVOICE_AWAITING_APPROVAL: 'INVOICE_AWAITING_APPROVAL',
    INVOICE_APPROVED: 'INVOICE_APPROVED',
    INVOICE_REJECTED: 'INVOICE_REJECTED',
    INVOICE_DUPLICATE_DETECTED: 'INVOICE_DUPLICATE_DETECTED',
    INVOICE_AWAITING_ACCOUNTING: 'INVOICE_AWAITING_ACCOUNTING',
    INVOICE_RETURNED_TO_REVIEW: 'INVOICE_RETURNED_TO_REVIEW',
    FINANCE_REQUEST_RAISED: 'FINANCE_REQUEST_RAISED',
    FINANCE_REQUEST_DECIDED: 'FINANCE_REQUEST_DECIDED',
    PAYROLL_AWAITING_APPROVAL: 'PAYROLL_AWAITING_APPROVAL',
    PAYROLL_APPROVED: 'PAYROLL_APPROVED',
    PAYROLL_REJECTED: 'PAYROLL_REJECTED',
    PAYMENT_BATCH_GENERATED: 'PAYMENT_BATCH_GENERATED',
    PAYMENT_BATCH_EXPORTED: 'PAYMENT_BATCH_EXPORTED',
    RECONCILIATION_UNMATCHED: 'RECONCILIATION_UNMATCHED',
    RECONCILIATION_COMPLETED: 'RECONCILIATION_COMPLETED',
    VENDOR_PAYMENT_COMPLETED: 'VENDOR_PAYMENT_COMPLETED',
};
export const EntityType = {
    TENANT: 'TENANT',
    GROUP: 'GROUP',
    COMPANY: 'COMPANY',
    REGION: 'REGION',
    VERTICAL: 'VERTICAL',
    BUSINESS_UNIT: 'BUSINESS_UNIT',
    LOCATION: 'LOCATION',
    DEPARTMENT: 'DEPARTMENT',
    USER: 'USER',
    ROLE: 'ROLE',
    VENDOR: 'VENDOR',
    BANK_ACCOUNT: 'BANK_ACCOUNT',
    PAYMENT_CARD: 'PAYMENT_CARD',
    NET_BANKING: 'NET_BANKING',
    LEDGER: 'LEDGER',
    TDS_SECTION: 'TDS_SECTION',
    EMPLOYEE: 'EMPLOYEE',
    LOAN: 'LOAN',
    INVOICE: 'INVOICE',
    PAYROLL_BATCH: 'PAYROLL_BATCH',
    PAYROLL_EMPLOYEE: 'PAYROLL_EMPLOYEE',
    PAYMENT_OBLIGATION: 'PAYMENT_OBLIGATION',
    APPROVAL_RULE: 'APPROVAL_RULE',
    APPROVAL_REQUEST: 'APPROVAL_REQUEST',
    FINANCE_REQUEST: 'FINANCE_REQUEST',
    PAYMENT_BATCH: 'PAYMENT_BATCH',
    BANK_STATEMENT: 'BANK_STATEMENT',
    BANK_TRANSACTION: 'BANK_TRANSACTION',
    RECONCILIATION: 'RECONCILIATION',
    MAIL_CONNECTION: 'MAIL_CONNECTION',
    MAIL_INGESTION: 'MAIL_INGESTION',
    AUTH: 'AUTH',
};
/**
 * Currencies a company can operate in.
 *
 * Every company books its own invoices in its own currency — an overseas
 * branch pays its local vendors locally — so this is the list a company's
 * `baseCurrency` is chosen from. Only two-decimal currencies are listed:
 * amounts are integer minor units at ×100 throughout, which JPY (0) or KWD (3)
 * would break.
 */
export const Currency = {
    INR: 'INR',
    USD: 'USD',
    EUR: 'EUR',
    GBP: 'GBP',
    AED: 'AED',
    SGD: 'SGD',
    AUD: 'AUD',
    CAD: 'CAD',
    CHF: 'CHF',
    SAR: 'SAR',
    QAR: 'QAR',
    MYR: 'MYR',
    THB: 'THB',
    ZAR: 'ZAR',
    NZD: 'NZD',
    HKD: 'HKD',
    CNY: 'CNY',
    SEK: 'SEK',
    NOK: 'NOK',
    DKK: 'DKK',
};
export const CURRENCIES = Object.values(Currency);
export function isCurrency(value) {
    return typeof value === 'string' && CURRENCIES.includes(value);
}
/**
 * Countries a company can be registered in (ISO 3166-1 alpha-2), each with the
 * currency it books in by default. India-only rules — GSTIN, PAN, IFSC, TDS,
 * NEFT/RTGS — apply only to companies in `IN`.
 */
export const COUNTRIES = [
    { code: 'IN', name: 'India', currency: Currency.INR },
    { code: 'AE', name: 'United Arab Emirates', currency: Currency.AED },
    { code: 'US', name: 'United States', currency: Currency.USD },
    { code: 'GB', name: 'United Kingdom', currency: Currency.GBP },
    { code: 'SG', name: 'Singapore', currency: Currency.SGD },
    { code: 'DE', name: 'Germany', currency: Currency.EUR },
    { code: 'FR', name: 'France', currency: Currency.EUR },
    { code: 'NL', name: 'Netherlands', currency: Currency.EUR },
    { code: 'IE', name: 'Ireland', currency: Currency.EUR },
    { code: 'AU', name: 'Australia', currency: Currency.AUD },
    { code: 'CA', name: 'Canada', currency: Currency.CAD },
    { code: 'CH', name: 'Switzerland', currency: Currency.CHF },
    { code: 'SA', name: 'Saudi Arabia', currency: Currency.SAR },
    { code: 'QA', name: 'Qatar', currency: Currency.QAR },
    { code: 'MY', name: 'Malaysia', currency: Currency.MYR },
    { code: 'TH', name: 'Thailand', currency: Currency.THB },
    { code: 'ZA', name: 'South Africa', currency: Currency.ZAR },
    { code: 'NZ', name: 'New Zealand', currency: Currency.NZD },
    { code: 'HK', name: 'Hong Kong', currency: Currency.HKD },
    { code: 'CN', name: 'China', currency: Currency.CNY },
    { code: 'SE', name: 'Sweden', currency: Currency.SEK },
    { code: 'NO', name: 'Norway', currency: Currency.NOK },
    { code: 'DK', name: 'Denmark', currency: Currency.DKK },
];
export const COUNTRY_CODES = COUNTRIES.map((country) => country.code);
/** The country whose rules (GSTIN, PAN, IFSC, TDS, NEFT/RTGS) the product was built on. */
export const HOME_COUNTRY = 'IN';
export function currencyForCountry(code) {
    return COUNTRIES.find((country) => country.code === code)?.currency ?? Currency.INR;
}
/**
 * Attachment types the extractor can read.
 *
 * Shared rather than repeated so the manual upload endpoint, the shared
 * company mailbox poller and the per-user Outlook connector can never disagree
 * about what is worth storing.
 */
export const SUPPORTED_INVOICE_CONTENT_TYPES = [
    'application/pdf',
    'image/jpeg',
    'image/png',
];
/** Mailbox providers a user can connect to pull invoices from. */
export const MailProvider = {
    OUTLOOK: 'OUTLOOK',
};
export const MAIL_PROVIDERS = Object.values(MailProvider);
/**
 * Health of a connected mailbox.
 *
 * `REVOKED` covers both a user disconnecting here and Microsoft withdrawing
 * consent at the source; `ERROR` is anything we can retry by reconnecting.
 */
export const MailConnectionStatus = {
    CONNECTED: 'CONNECTED',
    EXPIRED: 'EXPIRED',
    REVOKED: 'REVOKED',
    ERROR: 'ERROR',
};
export const MAIL_CONNECTION_STATUSES = Object.values(MailConnectionStatus);
/** Outcome of pulling one email. `SKIPPED` is an expected result, not a fault. */
export const MailIngestionStatus = {
    PENDING: 'PENDING',
    PROCESSING: 'PROCESSING',
    PARTIAL: 'PARTIAL',
    COMPLETED: 'COMPLETED',
    SKIPPED: 'SKIPPED',
    FAILED: 'FAILED',
};
export const MAIL_INGESTION_STATUSES = Object.values(MailIngestionStatus);
/** Outcome of reading one attachment out of a pulled email. */
export const MailAttachmentStatus = {
    QUEUED: 'QUEUED',
    EXTRACTING: 'EXTRACTING',
    READY_FOR_REVIEW: 'READY_FOR_REVIEW',
    SKIPPED: 'SKIPPED',
    FAILED: 'FAILED',
};
export const MAIL_ATTACHMENT_STATUSES = Object.values(MailAttachmentStatus);
/**
 * Why a pulled email produced no invoice.
 *
 * Stored rather than dropped: this is the only way a user can answer "why
 * didn't my invoice come in?" without reading a server log.
 */
export const MailSkipReason = {
    NO_ATTACHMENTS: 'NO_ATTACHMENTS',
    UNSUPPORTED_ATTACHMENTS: 'UNSUPPORTED_ATTACHMENTS',
    SENDER_NOT_ALLOWED: 'SENDER_NOT_ALLOWED',
    SUBJECT_NOT_MATCHED: 'SUBJECT_NOT_MATCHED',
    ATTACHMENT_TOO_LARGE: 'ATTACHMENT_TOO_LARGE',
    COMPANY_ACCESS_LOST: 'COMPANY_ACCESS_LOST',
};
export const MAIL_SKIP_REASONS = Object.values(MailSkipReason);
/** Printed verbatim on the Invoice Mailbox screen. */
export const MAIL_SKIP_REASON_LABELS = {
    NO_ATTACHMENTS: 'The email carried no attachments',
    UNSUPPORTED_ATTACHMENTS: 'No attachment was a PDF, JPEG or PNG',
    SENDER_NOT_ALLOWED: 'The sender is not on your allow list',
    SUBJECT_NOT_MATCHED: 'The subject matched none of your keywords',
    ATTACHMENT_TOO_LARGE: 'Every attachment was too large to read',
    COMPANY_ACCESS_LOST: 'You no longer have access to the company this would go to',
};
/** How a sync run ended. `PARTIAL` means some emails failed and the rest did not. */
export const MailSyncOutcome = {
    SUCCESS: 'SUCCESS',
    PARTIAL: 'PARTIAL',
    FAILED: 'FAILED',
};
/** Whether a sync is currently claimed. Doubles as the concurrency lock. */
export const MailSyncState = {
    IDLE: 'IDLE',
    RUNNING: 'RUNNING',
};
/** Which part of a message a company routing rule matches on. */
export const MailRouteMatch = {
    SENDER: 'SENDER',
    SUBJECT: 'SUBJECT',
    TO: 'TO',
};
export const MAIL_ROUTE_MATCHES = Object.values(MailRouteMatch);
// ── Loans the company has taken ─────────────────────────────
/** A loan's life: entered as a draft, activated to start its EMIs, closed once repaid. */
export const LoanStatus = {
    DRAFT: 'DRAFT',
    ACTIVE: 'ACTIVE',
    CLOSED: 'CLOSED',
    CANCELLED: 'CANCELLED',
};
export const LOAN_STATUSES = Object.values(LoanStatus);
export const LoanKind = {
    TERM: 'TERM',
    WORKING_CAPITAL: 'WORKING_CAPITAL',
    VEHICLE: 'VEHICLE',
    EQUIPMENT: 'EQUIPMENT',
    OTHER: 'OTHER',
};
export const LOAN_KINDS = Object.values(LoanKind);
/** Who lent it. TDS on interest (194A) applies when the lender is not a bank. */
export const LenderKind = {
    BANK: 'BANK',
    NBFC: 'NBFC',
    OTHER: 'OTHER',
};
export const LENDER_KINDS = Object.values(LenderKind);
export const RepaymentFrequency = {
    MONTHLY: 'MONTHLY',
    QUARTERLY: 'QUARTERLY',
    HALF_YEARLY: 'HALF_YEARLY',
    YEARLY: 'YEARLY',
};
export const REPAYMENT_FREQUENCIES = Object.values(RepaymentFrequency);
/** EMI: equal instalments on a reducing balance. BULLET: interest each period, principal at the end. */
export const RepaymentStructure = {
    EMI: 'EMI',
    BULLET: 'BULLET',
};
export const REPAYMENT_STRUCTURES = Object.values(RepaymentStructure);
/** During a moratorium: pay the interest only, or pay nothing and add it to the loan. */
export const MoratoriumMode = {
    INTEREST_ONLY: 'INTEREST_ONLY',
    CAPITALISE: 'CAPITALISE',
};
export const MORATORIUM_MODES = Object.values(MoratoriumMode);
/** AUTO_DEBIT: the bank takes the EMI (ECS/NACH). BANK_TRANSFER: paid through a payment batch. */
export const RepaymentMode = {
    AUTO_DEBIT: 'AUTO_DEBIT',
    BANK_TRANSFER: 'BANK_TRANSFER',
};
export const REPAYMENT_MODES = Object.values(RepaymentMode);
/** An instalment or charge: on the schedule, released into the payment flow, or paid (matched to the bank). */
export const InstalmentStatus = {
    SCHEDULED: 'SCHEDULED',
    RELEASED: 'RELEASED',
    PAID: 'PAID',
};
export const LoanChargeKind = {
    PROCESSING_FEE: 'PROCESSING_FEE',
    PENAL: 'PENAL',
};
/** What a row of a loan's track shows, worked out from its status and its obligation. */
export const InstalmentDisplayStatus = {
    SCHEDULED: 'SCHEDULED',
    IN_PAYMENT_QUEUE: 'IN_PAYMENT_QUEUE',
    AWAITING_DEBIT: 'AWAITING_DEBIT',
    OVERDUE: 'OVERDUE',
    PAID: 'PAID',
};
//# sourceMappingURL=enums.js.map